import liveswitch from "fm.liveswitch";
import ChannelConnectionEvents from "./ChannelConnectionEvents";
import ConnectionData from "./ConnectionData";
import CanvasDrawing from "../drawing/CanvasDrawing";
import { generateRandomColor } from "../utils/ColorUtils";
import { v4 } from "uuid";

export default class SFUConnection {
    private channel?: liveswitch.Channel;
    private localMedia?: liveswitch.LocalMedia;
    private videoDom!: HTMLElement | null;
    private layoutManager!: liveswitch.DomLayoutManager;

    private focusedView: string | null = null;
    private unfocusedOverlays: ConnectionData[] = [];
    private screenshareOverlay?: ConnectionData;
    public readonly canvasDrawing: CanvasDrawing;

    private upstreamConnection?: liveswitch.SfuUpstreamConnection;
    private closedDownstreamIds = new Set<string>();
    private localVideoEnabled = true;
    private localAudioEnabled = true;
    private mediaUpdateConnection?: liveswitch.SfuUpstreamConnection;
    private mediaUpdatePending = false;
    private isClosed = false;
    private readonly handleUnexpectedVideoUnmute = () => {
        if (!this.localVideoEnabled) {
            console.warn("Local video was unexpectedly unmuted; restoring the user's choice.");
            this.applyLocalMediaState();
        }
    };
    private readonly handleUnexpectedAudioUnmute = () => {
        if (!this.localAudioEnabled) {
            console.warn("Local audio was unexpectedly unmuted; restoring the user's choice.");
            this.applyLocalMediaState();
        }
    };

    public connections: {
        [key: string]: ConnectionData;
    } = {};

    constructor(private events: ChannelConnectionEvents) {
        this.canvasDrawing = new CanvasDrawing();
    }

    public openConnection(
        channel: liveswitch.Channel,
        localMedia: liveswitch.LocalMedia
    ): void {
        this.channel = channel;
        this.isClosed = false;
        this.removeLocalMediaStateGuards();
        this.localMedia = localMedia;
        this.localVideoEnabled = !localMedia.getVideoMuted();
        this.localAudioEnabled = !localMedia.getAudioMuted();
        localMedia.addOnVideoUnmuted(this.handleUnexpectedVideoUnmute);
        localMedia.addOnAudioUnmuted(this.handleUnexpectedAudioUnmute);

        if (!this.videoDom) {
            this.videoDom = document.getElementById("video") as HTMLElement;
            if (!this.videoDom) {
                throw new Error("Couldn't find Video element in page");
            }
            this.layoutManager = new liveswitch.DomLayoutManager(this.videoDom);
            this.layoutManager.setMode(liveswitch.LayoutMode.Inline);
            this.layoutManager.addOnLayout((_) => {
                console.log("Layout updated, connections:", Object.values(this.connections));
                this.events.notify("onvideolayout", Object.values(this.connections));
            });
        }

        // Handle both SFU and MCU connections
        this.channel.addOnRemoteUpstreamConnectionOpen((connectionInfo) => {
            console.log("Remote connection opened:", connectionInfo.getType(), connectionInfo.getUserAlias());
            if (connectionInfo.getType() === "sfu") {
                this.openDownstreamConnection(connectionInfo);
            } else if (connectionInfo.getType() === "mcu") {
                this.openMcuDownstreamConnection(connectionInfo);
            }
        });

        this.openUpstreamConnection();

        // Handle existing connections
        for (let remoteConnectionInfo of this.channel.getRemoteUpstreamConnectionInfos()) {
            console.log("Existing remote connection:", remoteConnectionInfo.getType(), remoteConnectionInfo.getUserAlias());
            if (remoteConnectionInfo.getType() === "sfu") {
                this.openDownstreamConnection(remoteConnectionInfo);
            } else if (remoteConnectionInfo.getType() === "mcu") {
                this.openMcuDownstreamConnection(remoteConnectionInfo);
            }
        }

        this.events.addListener("onscreenshareupdate", (overlay: ConnectionData | null) => {
            if (this.focusedView) {
                this.focusView(null);
            }

            if (!overlay) {
                this.clearScreenshareConnections();
            } else if (overlay.roles?.includes("screenshare")) {
                this.screenshareOverlay = overlay;
                // Remote shares are already added in Connecting — only add if missing (local share)
                if (!this.connections[overlay.id]) {
                    this.addConnection(overlay);
                }
            }

            this.refreshLayout();
        });
    }

    public closeConnection(): void {
        this.isClosed = true;
        this.removeLocalMediaStateGuards();
        this.upstreamConnection?.close();
        this.upstreamConnection = undefined;
        this.mediaUpdateConnection = undefined;
        this.mediaUpdatePending = false;
    }

    private removeLocalMediaStateGuards(): void {
        if (!this.localMedia) return;
        this.localMedia.removeOnVideoUnmuted(this.handleUnexpectedVideoUnmute);
        this.localMedia.removeOnAudioUnmuted(this.handleUnexpectedAudioUnmute);
    }

    private refreshLayout() {
        try {
            this.layoutManager?.layout();
        } catch (e) {
            console.warn("Layout refresh failed", e);
        }
        this.events.notify("onvideolayout", Object.values(this.connections));
    }

    private clearScreenshareConnections() {
        const screenConnections = Object.values(this.connections).filter(
            (c) => c.isScreen || c.roles?.includes("screenshare")
        );
        for (const c of screenConnections) {
            this.removeConnection(c.id);
        }
        this.unfocusedOverlays = this.unfocusedOverlays.filter(
            (c) => !c.isScreen && !c.roles?.includes("screenshare")
        );
        this.screenshareOverlay = undefined;
    }

    public setLocalVideoEnabled(enabled: boolean): void {
        this.localVideoEnabled = enabled;
        this.applyLocalMediaState();
    }

    public setLocalAudioEnabled(enabled: boolean): void {
        this.localAudioEnabled = enabled;
        this.applyLocalMediaState();
    }

    /**
     * Keep one explicit desired media state instead of toggling whatever state
     * the current connection happens to report. LiveSwitch can replace or
     * refresh an upstream connection while participants join, so the desired
     * state must survive that lifecycle.
     */
    private applyLocalMediaState(): void {
        const videoMuted = !this.localVideoEnabled;
        const audioMuted = !this.localAudioEnabled;

        // Apply immediately at the media source. This guarantees that frames
        // cannot leak while a signalling update is queued.
        if (this.localMedia?.getVideoMuted() !== videoMuted) {
            this.localMedia?.setVideoMuted(videoMuted);
        }
        if (this.localMedia?.getAudioMuted() !== audioMuted) {
            this.localMedia?.setAudioMuted(audioMuted);
        }

        const connection = this.upstreamConnection;
        if (!connection) return;

        // Serialize updates. Otherwise two quick button presses can complete
        // out of order and leave the server with the older mute state.
        if (this.mediaUpdateConnection === connection) {
            this.mediaUpdatePending = true;
            return;
        }

        const config = connection.getConfig();
        config.setLocalVideoMuted(videoMuted);
        config.setLocalAudioMuted(audioMuted);
        this.mediaUpdateConnection = connection;

        const finish = (error?: liveswitch.Exception) => {
            // Ignore completion from a connection that has since been replaced.
            if (this.mediaUpdateConnection !== connection) return;

            this.mediaUpdateConnection = undefined;
            if (error) {
                console.error("Failed to update local media state:", error.message);
            }

            if (this.mediaUpdatePending) {
                this.mediaUpdatePending = false;
                this.applyLocalMediaState();
            }
        };

        connection.update(config).then(
            () => finish(),
            (error) => finish(error)
        );
    }

    public toggleFocusView(viewId: string) {
        if (!this.focusedView) {
            this.unfocusedOverlays = Object.values(this.connections).filter(x => x.id !== viewId);

            // Hide other tiles from layout only — keep their DOM/views intact for restore
            for (const overlay of this.unfocusedOverlays) {
                this.removeConnection(overlay.id, { permanent: false });
            }
            this.focusedView = viewId;
            this.refreshLayout();
        } else {
            // Restore all previously removed views
            for (const overlay of this.unfocusedOverlays) {
                this.addConnection(overlay);
            }

            this.unfocusedOverlays = [];
            this.focusedView = null;
            this.refreshLayout();
        }
    }

    public focusView(viewId: string | null) {
        if (viewId) {
            // Already focused on this tile — no-op
            if (this.focusedView === viewId) {
                this.events.notify("onviewfocus", this.connections[viewId]);
                return;
            }

            // Switching focus: restore any previously hidden tiles first
            if (this.focusedView && this.unfocusedOverlays.length) {
                for (const overlay of this.unfocusedOverlays) {
                    this.addConnection(overlay);
                }
                this.unfocusedOverlays = [];
            }

            this.unfocusedOverlays = Object.values(this.connections).filter(
                (x) => x.id !== viewId
            );

            // Hide other tiles from layout only — do NOT destroy/detach their views
            for (const overlay of this.unfocusedOverlays) {
                this.removeConnection(overlay.id, { permanent: false });
            }

            this.focusedView = viewId;
            this.refreshLayout();
            this.events.notify("onviewfocus", this.connections[viewId]);
        } else {
            // Restore all previously hidden views
            for (const overlay of this.unfocusedOverlays) {
                this.addConnection(overlay);
            }

            this.unfocusedOverlays = [];
            this.focusedView = null;
            this.refreshLayout();
            this.events.notify("onviewfocus", null);
        }
    }

    public getFocusedViewId(): string | null {
        return this.focusedView;
    }

    public getUnfocusedConnections(): ConnectionData[] {
        return this.unfocusedOverlays.slice();
    }

    /** Active layout tiles plus any temporarily hidden during pin/annotate. */
    public getAllConnections(): ConnectionData[] {
        return [
            ...Object.values(this.connections),
            ...this.unfocusedOverlays,
        ];
    }

    private addConnection(connectionData: ConnectionData) {
        console.log("Adding connection:", connectionData.userAlias);
        this.connections[connectionData.id] = connectionData;

        const view = connectionData.view as HTMLElement | undefined;
        if (view) {
            // Undo temporary focus-hide / leftover screenshare styles
            view.style.display = "";
            view.style.visibility = "";
            view.style.opacity = "";
        }

        const video = connectionData.view.querySelector('video') as HTMLVideoElement | null;
        if (video) {
            const mirrorSelf = connectionData.isLocal && !connectionData.isScreen;
            video.classList.toggle('self-mirrored', mirrorSelf);
            // Clear inline transform so CSS can control mirroring + layout modes
            video.style.transform = '';
            try {
                if (video.paused) video.play().catch(() => {});
            } catch {}
        }
        
        if (connectionData.isLocal && !connectionData.isScreen) {
            this.layoutManager.setLocalView(connectionData.view);
        } else {
            this.layoutManager.addRemoteView(
                connectionData.id,
                connectionData.view
            );
        }
    }

    /**
     * @param permanent When true (default), fully detach the tile DOM — used for
     * connection close / screenshare cleanup. When false, only remove from the
     * layout manager so annotation pin/unpin can restore the same view elements.
     */
    private removeConnection(id: string, options: { permanent?: boolean } = {}) {
        const permanent = options.permanent !== false;
        const connection = this.connections[id];
        if (!connection) return;

        try {
            if (connection.isLocal && !connection.isScreen) {
                this.layoutManager.unsetLocalView();
            } else {
                this.layoutManager.removeRemoteView(connection.id);
            }
        } catch (e) {
            console.warn("Layout manager remove failed", id, e);
        }

        const view = connection.view as HTMLElement | undefined;
        if (permanent && view) {
            // Force-detach so stopped screen shares don't leave a blank slot
            const overlay = view.querySelector("#overlay");
            overlay?.remove();
            if (view.parentElement) {
                view.parentElement.removeChild(view);
            }
            view.style.display = "none";
        }

        delete this.connections[id];
    }

    private handleDownstreamClosed(
        remoteConnectionInfo: liveswitch.ConnectionInfo,
        connectionData: ConnectionData,
        remoteMedia: liveswitch.RemoteMedia
    ) {
        const id = remoteConnectionInfo.getId();
        if (this.closedDownstreamIds.has(id)) {
            return;
        }
        this.closedDownstreamIds.add(id);

        const existing = this.connections[id];
        const wasScreen =
            connectionData.isScreen ||
            connectionData.roles?.includes("screenshare") ||
            this.screenshareOverlay?.id === id ||
            !!existing?.isScreen ||
            !!existing?.roles?.includes("screenshare");

        if (this.focusedView === id) {
            this.focusView(null);
        } else if (this.focusedView) {
            this.unfocusedOverlays = this.unfocusedOverlays.filter((x) => x.id !== id);
        }

        this.removeConnection(id);

        if (wasScreen) {
            this.clearScreenshareConnections();
            this.events.notify("onscreenshareupdate", null);
        }

        this.events.notify("onconnectionclose", connectionData);
        this.refreshLayout();

        try {
            remoteMedia.destroy();
        } catch (e) {
            console.warn("Remote media destroy failed", e);
        }
    }

    private readVideoSize(remoteConnectionInfo: liveswitch.ConnectionInfo): {
        width: number;
        height: number;
        isScreen: boolean;
    } {
        let width = 640;
        let height = 480;
        let isScreen = false;
        try {
            const videoStream = remoteConnectionInfo.getVideoStream();
            isScreen =
                remoteConnectionInfo.getType() === "sfu" &&
                videoStream?.getSendContent?.() === "screen";
            const encodings = videoStream?.getSendEncodings?.() || [];
            if (encodings[0]) {
                width = encodings[0].getWidth?.() || width;
                height = encodings[0].getHeight?.() || height;
            }
        } catch (e) {
            console.warn("Could not read remote video metadata", e);
        }
        return { width, height, isScreen };
    }

    private openUpstreamConnection() {
        if (this.isClosed) return;

        const audioStream = new liveswitch.AudioStream(this.localMedia!);
        const videoStream = new liveswitch.VideoStream(this.localMedia!);
        const dataStream = new liveswitch.DataStream([
            this.canvasDrawing.sendDataChannel
        ]);

        this.upstreamConnection = this.channel!.createSfuUpstreamConnection(
            audioStream,
            videoStream,
            dataStream
        );

        // A replacement upstream connection must start with the user's current
        // choice, rather than LiveSwitch's default unmuted configuration.
        const initialConfig = this.upstreamConnection.getConfig();
        initialConfig.setLocalVideoMuted(!this.localVideoEnabled);
        initialConfig.setLocalAudioMuted(!this.localAudioEnabled);

        this.upstreamConnection.addOnStateChange((conn) => {
            const state = conn.getState();

            console.log(
                `Upstream connection is ${new liveswitch.ConnectionStateWrapper(
                    state
                ).toString()}`
            );

            // State events from a failed/replaced connection must not mutate the
            // current session or open another competing upstream.
            if (conn !== this.upstreamConnection || this.isClosed) {
                return;
            }

            // Device restarts and renegotiation may rewrite track mute flags.
            // Reassert the user's choice whenever the active upstream changes
            // state, and sync signalling if its config also drifted.
            const desiredVideoMuted = !this.localVideoEnabled;
            const desiredAudioMuted = !this.localAudioEnabled;
            if (
                this.localMedia!.getVideoMuted() !== desiredVideoMuted ||
                this.localMedia!.getAudioMuted() !== desiredAudioMuted ||
                conn.getConfig().getLocalVideoMuted() !== desiredVideoMuted ||
                conn.getConfig().getLocalAudioMuted() !== desiredAudioMuted
            ) {
                this.applyLocalMediaState();
            }

            const size = this.localMedia!.getVideoSize();
            const messageId = v4();
            const connectionData: ConnectionData = {
                id: conn.getId(),
                userId: conn.getUserId(),
                deviceId: this.channel!.getDeviceId().replace(/-/g, ''),
                userAlias: this.channel!.getUserAlias(),
                roles: [...this.channel!.getClientRoles(), "local", `m_${messageId}`],
                color: generateRandomColor(
                    Object.values(this.connections).map((x) => x.color)
                ),
                isLocal: true,
                isScreen: false,
                isXR: false,
                view: this.localMedia!.getView(),
                videoWidth: size.getWidth(),
                videoHeight: size.getHeight(),
            };

            if (state === liveswitch.ConnectionState.Connecting) {
                this.addConnection(connectionData);
                this.events.notify("onconnectionopen", connectionData);

                this.canvasDrawing.myConnectionData = connectionData;
            } else if (state === liveswitch.ConnectionState.Closing || state === liveswitch.ConnectionState.Failing) {
                this.events.notify("onconnectionclose", connectionData);
            } else if (state === liveswitch.ConnectionState.Failed) {
                this.openUpstreamConnection();
            }
        });

        this.upstreamConnection.open();
    }

    private openDownstreamConnection(
        remoteConnectionInfo: liveswitch.ConnectionInfo
    ) {
        console.log("Opening downstream connection for:", remoteConnectionInfo.getUserAlias());
        
        const remoteMedia = new liveswitch.RemoteMedia(true, true);
        const audioStream = new liveswitch.AudioStream(remoteMedia);
        const videoStream = new liveswitch.VideoStream(remoteMedia);
        const dataStream = new liveswitch.DataStream([
            this.canvasDrawing.createReceiveDataChannel()
        ]);

        const downstreamConnection =
            this.channel!.createSfuDownstreamConnection(
                remoteConnectionInfo,
                audioStream,
                videoStream,
                dataStream
            );

        downstreamConnection.addOnStateChange((conn) => {
            const state = conn.getState();

            console.log(
                `Downstream connection ${conn.getId()} is ${new liveswitch.ConnectionStateWrapper(
                    state
                ).toString()}`
            );

            const { width, height, isScreen } = this.readVideoSize(remoteConnectionInfo);
            const isXR = false;

            const connectionData: ConnectionData = {
                id: remoteConnectionInfo.getId(),
                userId: remoteConnectionInfo.getUserId(),
                deviceId: remoteConnectionInfo.getDeviceId(),
                userAlias: remoteConnectionInfo.getUserAlias(),
                roles: isScreen ? ["remote", "screenshare"] : ["remote"],
                color: generateRandomColor(
                    Object.values(this.connections).map((x) => x.color)
                ),
                isLocal: false,
                isScreen,
                isXR,
                view: remoteMedia.getView(),
                videoWidth: width,
                videoHeight: height
            };

            if (state === liveswitch.ConnectionState.Connecting) {
                if (this.focusedView) {
                    this.unfocusedOverlays.push(connectionData);
                } else {
                    this.addConnection(connectionData);
                }

                this.events.notify("onconnectionopen", connectionData);
                if (isScreen) {
                    this.screenshareOverlay = connectionData;
                    this.events.notify("onscreenshareupdate", connectionData);
                }
                this.refreshLayout();
            } else if (
                state === liveswitch.ConnectionState.Closing ||
                state === liveswitch.ConnectionState.Failing ||
                state === liveswitch.ConnectionState.Closed
            ) {
                this.handleDownstreamClosed(remoteConnectionInfo, connectionData, remoteMedia);
            }
        });

        downstreamConnection.open();
    }

    private openMcuDownstreamConnection(remoteConnectionInfo: liveswitch.ConnectionInfo) {
        console.log("Opening MCU downstream connection for:", remoteConnectionInfo.getUserAlias());
        
        const remoteMedia = new liveswitch.RemoteMedia(true, true);
        const audioStream = new liveswitch.AudioStream(remoteMedia);
        const videoStream = new liveswitch.VideoStream(remoteMedia);

        const downstreamConnection = this.channel!.createSfuDownstreamConnection(
            remoteConnectionInfo,
            audioStream,
            videoStream
        );

        downstreamConnection.addOnStateChange((conn: liveswitch.ManagedConnection) => {
            const state = conn.getState();

            console.log(
                `MCU Downstream connection ${conn.getId()} is ${new liveswitch.ConnectionStateWrapper(
                    state
                ).toString()}`
            );

            const { width, height, isScreen } = this.readVideoSize(remoteConnectionInfo);
            const isXR = false;

            const connectionData: ConnectionData = {
                id: remoteConnectionInfo.getId(),
                userId: remoteConnectionInfo.getUserId(),
                deviceId: remoteConnectionInfo.getDeviceId(),
                userAlias: remoteConnectionInfo.getUserAlias(),
                roles: isScreen ? ["mcu", "screenshare"] : ["mcu"],
                color: generateRandomColor(
                    Object.values(this.connections).map((x) => x.color)
                ),
                isLocal: false,
                isScreen,
                isXR,
                view: remoteMedia.getView(),
                videoWidth: width,
                videoHeight: height
            };

            if (state === liveswitch.ConnectionState.Connecting) {
                if (this.focusedView) {
                    this.unfocusedOverlays.push(connectionData);
                } else {
                    this.addConnection(connectionData);
                }

                this.events.notify("onconnectionopen", connectionData);
                if (isScreen) {
                    this.screenshareOverlay = connectionData;
                    this.events.notify("onscreenshareupdate", connectionData);
                }
                this.refreshLayout();
            } else if (
                state === liveswitch.ConnectionState.Closing ||
                state === liveswitch.ConnectionState.Failing ||
                state === liveswitch.ConnectionState.Closed
            ) {
                this.handleDownstreamClosed(remoteConnectionInfo, connectionData, remoteMedia);
            }
        });

        downstreamConnection.open();
    }
}
